import { useState } from 'react';
import { FaFlagCheckered, FaClock, FaRoute, FaTimes, FaPlay } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import ImageWithToken from './ImageWithToken';
import ApiService from '../ApiService';
import '../styles/RouteNavigationSheet.css';

const RouteNavigationSheet = ({ route, places, user, onClose, onPlaceClick, originLabel, destLabel }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!route) return null;

  const safePlaces = places || [];

  const handleSkip = (e) => {
    e.stopPropagation();
    setCurrentIndex(i => Math.min(i + 1, safePlaces.length - 1));
  };

  const handleUndo = (e) => {
    e.stopPropagation();
    setCurrentIndex(i => Math.max(i - 1, 0));
  };

  const visiblePlaces = expanded
    ? safePlaces
    : safePlaces.length > 0 ? safePlaces.slice(currentIndex, currentIndex + 1) : [];

  return (
    <div className={`rns-sheet ${expanded ? 'rns-expanded' : ''}`}>
      <div className="rns-handle-row" onClick={() => setExpanded(e => !e)}>
        <div className="rns-handle" />
        <button className="rns-close" onClick={(e) => { e.stopPropagation(); onClose(); }}>
          <FaTimes />
        </button>
      </div>

      <div className="rns-stats-row">
        <div className="rns-stats">
          <div className="rns-stat">
            <FaFlagCheckered className="rns-stat-icon" />
            <span className="rns-stat-label">{safePlaces.length} {t('route_info.stops')}</span>
          </div>
          <div className="rns-stat">
            <FaClock className="rns-stat-icon" />
            <span className="rns-stat-label">{route.static_duration_text}</span>
          </div>
          <div className="rns-stat">
            <FaRoute className="rns-stat-icon" />
            <span className="rns-stat-label">{route.distance_text}</span>
          </div>
        </div>
        <div className="rns-actions">
          <button className="rns-btn-skip" onClick={handleSkip} disabled={currentIndex >= safePlaces.length - 1}>
            {t('route_navigation.skip')}
          </button>
          <button className="rns-btn-undo" onClick={handleUndo} disabled={currentIndex === 0}>
            {t('route_navigation.undo')}
          </button>
        </div>
      </div>

      <div className="rns-place-list">
        {/* Origin */}
        {expanded && originLabel && (
          <div className="rns-endpoint-item">
            <div className="rns-timeline">
              <div className="rns-endpoint-dot rns-endpoint-dot-start" />
              {(safePlaces.length > 0 || destLabel) && <div className="rns-line" />}
            </div>
            <div className="rns-endpoint-content">
              <span className="rns-place-name">{originLabel}</span>
            </div>
          </div>
        )}

        {/* Intermediate places */}
        {visiblePlaces.map((place, offsetIdx) => {
          const absoluteIdx = expanded ? offsetIdx : currentIndex;
          const isCurrent = absoluteIdx === currentIndex;
          const placeName = place.description?.name || t('story_modal.unknown_place');
          const vicinity = place.description?.vicinity || '';
          const hasLineBelow = expanded
            ? (offsetIdx < visiblePlaces.length - 1 || !!destLabel)
            : false;

          return (
            <div key={place.reference} className="rns-place-item">
              <div className="rns-timeline">
                <div className={`rns-circle ${isCurrent ? 'rns-circle-active' : ''}`}>
                  {absoluteIdx + 1}
                </div>
                {hasLineBelow && <div className="rns-line" />}
              </div>
              <div className="rns-place-content" onClick={() => onPlaceClick(place)}>
                {isCurrent ? (
                  <div className="rns-play-btn">
                    <FaPlay className="rns-play-icon" />
                  </div>
                ) : (
                  <div className="rns-thumb">
                    <ImageWithToken
                      imageUrl={ApiService.create_place_photo_url(place)}
                      user={user}
                      alt={placeName}
                      className="rns-thumb-img"
                    />
                  </div>
                )}
                <div className="rns-place-text">
                  <span className="rns-place-name">{placeName}</span>
                  {vicinity && <span className="rns-place-vicinity">{vicinity}</span>}
                </div>
              </div>
            </div>
          );
        })}

        {/* Destination */}
        {expanded && destLabel && (
          <div className="rns-endpoint-item">
            <div className="rns-timeline">
              <div className="rns-endpoint-dot rns-endpoint-dot-end" />
            </div>
            <div className="rns-endpoint-content">
              <span className="rns-place-name">{destLabel}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteNavigationSheet;
